<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <Toast :position="isDesktop ? 'top-right' : 'top-center'">
      <template #message="slotProps">
        <ErrorToastMessage :message="slotProps.message" />
      </template>
    </Toast>
    <RouterView />
  </div>
</template>

<script setup lang="ts">
import { RouterView } from "vue-router";
import { watch, onMounted } from "vue";
import { useToast } from "primevue";
import { usePrimeVue } from "primevue/config";
import { setToastInstance } from "@shared/helpers/toastHelpers";
import ErrorToastMessage from "@shared/components/ErrorToastMessage.vue";
import { useBreakpoint } from "@/composables/useBreakpoint";
import { useI18n } from "@/locales/useI18n";

const toast = useToast();
setToastInstance(toast);

// Keep PrimeVue's date-component locale (month/day names) in sync with the app language.
const primevue = usePrimeVue();
const { t, currentLocale } = useI18n();

function syncPrimeVueLocale() {
  const locale = primevue.config.locale;
  if (!locale) return;
  const m = t.value.months;
  const d = t.value.days;
  const monthNames = [
    m.january, m.february, m.march, m.april, m.may, m.june,
    m.july, m.august, m.september, m.october, m.november, m.december,
  ];
  // Mutate in place so PrimeVue's reactive config picks up the change without a type-incompatible reassign.
  locale.firstDayOfWeek = 1;
  locale.monthNames = monthNames;
  locale.monthNamesShort = monthNames.map((name) => name.slice(0, 3));
  locale.dayNames = [d.sunday, d.monday, d.tuesday, d.wednesday, d.thursday, d.friday, d.saturday];
  locale.dayNamesShort = [d.sun, d.mon, d.tue, d.wed, d.thu, d.fri, d.sat];
  locale.dayNamesMin = [d.sun, d.mon, d.tue, d.wed, d.thu, d.fri, d.sat];
}

watch(currentLocale, syncPrimeVueLocale, { immediate: true });

const { isDesktop } = useBreakpoint();

function setViewportMeta(desktop: boolean) {
  const viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) return;

  viewport.setAttribute(
    "content",
    desktop
      ? "width=device-width, initial-scale=1.0"
      : "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover",
  );
}

onMounted(() => {
  setViewportMeta(isDesktop.value);
});

watch(isDesktop, (desktop) => {
  setViewportMeta(desktop);
});
</script>
